import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { RefreshCw, Loader2, Info, ArrowUp, ArrowDown } from 'lucide-react';
import { automationService, type PaperTradingTrade, type Strategy } from '@/services/strategyService';
import { signalRService } from '@/services/signalRService';
import { TradeChartDialog } from './TradeChartDialog';

/**
 * Forex PAPER automated dashboard — the forex analogue of the equity PaperTradingPage's
 * automated view, but sourced from StrategyTrade docs (the forex paper broker is the in-process
 * simulator, so there is no broker account/positions endpoint to read). Shows the enabled
 * forex strategies, currently-open simulated positions (with live unrealized P&L), recent closed
 * trades, and per-strategy P&L — with forex-aware formatting (5-decimal rates / 3 for JPY, lot
 * sizing, quote-currency P&L).
 *
 * Fully push-based (no polling): after an initial REST load, it joins each forex rule's scanner-hub
 * group and applies trade.opened/updated/closed events. TradeGrain broadcasts the full trade on
 * every bar with a fresh LastPrice, so open-position unrealized P&L updates live.
 */

const LISTENER_ID = 'forex-automation-panel';

// SignalR pushes the raw StrategyTrade with enums serialized as NUMBERS — the
// hub JSON protocol has no JsonStringEnumConverter (unlike the REST API, which
// emits strings). Map them to the string forms the REST snapshot uses so pushed
// and fetched trades are shape-identical. Tolerant of already-string values, so
// it stays correct if the hub later gains the converter. Orders mirror the C# enums.
const E_STATUS = ['Pending', 'Open', 'Closed', 'Cancelled', 'EntryPending', 'ExitPending'];
const E_DIRECTION = ['Long', 'Short'];
const E_ASSET = ['Equity', 'Forex', 'Crypto'];
const E_ENV = ['Paper', 'Live'];
const E_EXIT = ['StopLoss', 'TakeProfit', 'TrailingStop', 'TimeStop', 'OppositeSignal', 'Manual', 'SessionEnd', 'BreakEven', 'OrphanReconciled', 'IlliquidityFlatten'];
const enumStr = (v: any, table: string[]): any => (typeof v === 'number' ? (table[v] ?? v) : v);

function normalizeTrade(raw: any): PaperTradingTrade {
  return {
    ...raw,
    status: enumStr(raw.status, E_STATUS),
    direction: enumStr(raw.direction, E_DIRECTION),
    assetClass: enumStr(raw.assetClass, E_ASSET),
    tradingEnvironment: enumStr(raw.tradingEnvironment, E_ENV),
    exitReason: raw.exitReason == null ? raw.exitReason : enumStr(raw.exitReason, E_EXIT),
  };
}

// Approximate spot quote→USD factors (mirrors backend ForexUsdConversion), used ONLY for
// P&L figures this component computes itself in quote currency — i.e. unrealized P&L on
// open positions. Realized P&L arrives from the backend ALREADY in USD (converted once at
// trade settlement, PR #257) and must NOT be converted again here.
const QUOTE_USD: Record<string, number> = { USD: 1, EUR: 1.08, GBP: 1.27, JPY: 0.0067, CHF: 1.12, CAD: 0.73, AUD: 0.65, NZD: 0.60 };
function toUsd(amountQuoteCcy: number, sym: string): number {
  return amountQuoteCcy * (QUOTE_USD[quoteCcy(sym)] ?? 1);
}

/** Unrealized P&L (quote ccy) for an OPEN position: (mark − entry) × units × dir. Null until a mark exists. */
function unrealized(t: PaperTradingTrade): number | null {
  const mark = t.lastPrice;
  if (mark == null || mark <= 0 || t.entryPrice == null) return null;
  const dir = t.direction === 'Short' ? -1 : 1;
  return (mark - t.entryPrice) * t.quantity * dir;
}

// ── Forex-aware formatting ───────────────────────────────────────
function isJpy(sym: string): boolean { return sym.toUpperCase().includes('JPY'); }
function quoteCcy(sym: string): string { return sym.length === 6 ? sym.slice(3, 6).toUpperCase() : ''; }

/** Currency-pair rate: JPY pairs to 3 decimals, everything else to 5 (standard FX convention). */
function fmtRate(n: number | null | undefined, sym: string): string {
  if (n == null) return '-';
  return n.toFixed(isJpy(sym) ? 3 : 5);
}

/** Units → lots (1 standard lot = 100,000 units). 10000 units → "0.10". */
function fmtLots(units: number | null | undefined): string {
  if (units == null) return '-';
  return (units / 100_000).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** P&L in the pair's quote currency (USD/JPY profit is in JPY, etc.). */
function fmtPnl(n: number | null | undefined, sym: string): string {
  if (n == null) return '-';
  const v = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${v} ${quoteCcy(sym)}`.trim();
}

/** P&L converted to USD and ROUNDED to cents — the single source of truth for both
 * the displayed value and its color, so a sub-cent value never shows a colored "$0.00". */
function usdPnl(n: number | null | undefined, sym: string): number | null {
  if (n == null) return null;
  return Math.round(toUsd(n, sym) * 100) / 100;
}

/** Already-USD P&L (realized, converted by the backend at settlement) rounded to cents. */
function usdDirect(n: number | null | undefined): number | null {
  if (n == null) return null;
  return Math.round(n * 100) / 100;
}

/** Rounded display for an already-USD amount, e.g. "$43.02" / "-$12.40". */
function fmtUsdDirect(n: number | null | undefined): string {
  const usd = usdDirect(n);
  if (usd == null) return '-';
  const sign = usd < 0 ? '-' : '';
  return `${sign}$${Math.abs(usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Rounded USD P&L for display, e.g. "$0.27" / "-$12.40". */
function fmtUsd(n: number | null | undefined, sym: string): string {
  const usd = usdPnl(n, sym);
  if (usd == null) return '-';
  const sign = usd < 0 ? '-' : '';
  return `${sign}$${Math.abs(usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pnlClass(n: number | null | undefined): string {
  if (n == null || n === 0) return 'text-muted-foreground';
  return n > 0 ? 'text-green-500' : 'text-red-500';
}

function fmtAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!(ms >= 0)) return '-';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ${m % 60}m` : `${Math.floor(h / 24)}d ${h % 24}h`;
}

function fmtNY(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso)).replace(',', '');
}

function dispSymbol(sym: string): string {
  return sym.length === 6 ? `${sym.slice(0, 3)}/${sym.slice(3, 6)}` : sym;
}

const OPEN_STATUSES = new Set(['Open', 'EntryPending', 'ExitPending']);

export const ForexAutomationPanel: React.FC = () => {
  const [trades, setTrades] = useState<PaperTradingTrade[]>([]);
  const [rules, setRules] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartTrade, setChartTrade] = useState<PaperTradingTrade | null>(null);
  const [closingIds, setClosingIds] = useState<Set<string>>(new Set());
  // Keyboard activation for the clickable trade rows (Enter/Space) — rows aren't
  // focusable by default, so we add role/tabIndex + this handler for parity with mouse.
  const rowActivate = (e: React.KeyboardEvent, t: PaperTradingTrade) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setChartTrade(t); }
  };

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Page through ALL rules (server caps pageSize at 100). Every forex rule
      // must be joined for live pushes — there's no polling fallback to catch
      // stragglers, and the page isn't asset-filtered so forex rules can sit on
      // any page when the user also has many equity rules.
      const allRules: Strategy[] = [];
      for (let page = 1; ; page++) {
        const r = await automationService.getRulesPage({ page, pageSize: 100 });
        allRules.push(...r.items);
        if (r.items.length < 100 || allRules.length >= r.total) break;
      }
      const t = await automationService.getPaperRecentTrades(200, 'Forex');
      setTrades(t);
      setRules(allRules.filter(x => (x.assetClass ?? 'Equity') === 'Forex'));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load forex paper data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Re-fetch ONLY the trade snapshot — used to reconcile after rule groups are
  // joined and after a reconnect, since pushes that fire while we're not yet
  // subscribed (or while the socket was down) are never replayed.
  const reloadTrades = useCallback(async () => {
    try {
      const t = await automationService.getPaperRecentTrades(200, 'Forex');
      setTrades(t);
    } catch { /* keep current trades on a transient fetch error */ }
  }, []);

  // Manual close of a single open trade (market exit via the trade grain). The strategy
  // may re-enter on its next signal — this just flattens the current position now.
  const closeTrade = useCallback(async (t: PaperTradingTrade) => {
    if (typeof window !== 'undefined' && !window.confirm(`Close ${dispSymbol(t.symbol)} now at market?`)) return;
    setClosingIds(prev => new Set(prev).add(t.id));
    try {
      await automationService.closePaperTrade(t.id);
      await reloadTrades();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Close failed');
    } finally {
      setClosingIds(prev => { const n = new Set(prev); n.delete(t.id); return n; });
    }
  }, [reloadTrades]);

  // Initial snapshot once. After this, all updates arrive via SignalR push.
  useEffect(() => { load(); }, [load]);

  // Reconcile a fresh trade snapshot after the scanner connection reconnects
  // (and its groups are rejoined) — closes the missed-push gap without polling.
  useEffect(() => {
    signalRService.onScannerReconnected(LISTENER_ID, () => { void reloadTrades(); });
    return () => signalRService.offScannerReconnected(LISTENER_ID);
  }, [reloadTrades]);

  // Live trade lifecycle: upsert pushed StrategyTrade docs into the trade list.
  // The payload is a camelCase superset of PaperTradingTrade (incl. a fresh
  // lastPrice every bar), so unrealized P&L tracks live with no polling.
  useEffect(() => {
    const upsert = (raw: any) => {
      if (!raw?.id) return;
      const t = normalizeTrade(raw);
      // We only join forex rule groups, but guard anyway so an equity trade
      // can never leak into this forex view.
      if (t.assetClass && t.assetClass !== 'Forex') return;
      // The trading-rule-{ruleId} group is shared by paper AND live runs of the
      // same rule. This is the PAPER dashboard (initial load filters to Paper),
      // so drop any live trade rather than mixing real-money P&L in here.
      if (t.tradingEnvironment && t.tradingEnvironment !== 'Paper') return;
      setTrades(prev => {
        const idx = prev.findIndex(x => x.id === t.id);
        if (idx >= 0) {
          const next = prev.slice();
          next[idx] = { ...prev[idx], ...t };
          return next;
        }
        return [t, ...prev];
      });
    };
    signalRService.addTradeLifecycleListener(LISTENER_ID, 'opened', upsert);
    signalRService.addTradeLifecycleListener(LISTENER_ID, 'updated', upsert);
    signalRService.addTradeLifecycleListener(LISTENER_ID, 'closed', upsert);
    return () => {
      signalRService.removeTradeLifecycleListener(LISTENER_ID, 'opened');
      signalRService.removeTradeLifecycleListener(LISTENER_ID, 'updated');
      signalRService.removeTradeLifecycleListener(LISTENER_ID, 'closed');
    };
  }, []);

  // Join/leave each forex rule's scanner-hub group as the rule set changes.
  // After all joins complete, reconcile the trade snapshot once: a trade event
  // that fired between the REST load and the join would otherwise be missed.
  const ruleIds = useMemo(() => rules.map(r => r.id).sort().join(','), [rules]);
  useEffect(() => {
    if (!ruleIds) return;
    const ids = ruleIds.split(',');
    let cancelled = false;
    (async () => {
      await Promise.all(ids.map(id => signalRService.joinTradingRuleGroup(id)));
      if (!cancelled) void reloadTrades();
    })();
    return () => { cancelled = true; ids.forEach(id => { void signalRService.leaveTradingRuleGroup(id); }); };
  }, [ruleIds, reloadTrades]);

  const open = useMemo(() => trades.filter(t => OPEN_STATUSES.has(t.status)), [trades]);
  const closed = useMemo(() => trades.filter(t => !OPEN_STATUSES.has(t.status) && t.exitTime != null), [trades]);

  // Realized P&L is backend-USD at settlement — sum as-is. (Converting again here would
  // shrink a $43 USDJPY win to $0.29.)
  const realized = useMemo(() => closed.reduce((s, t) => s + (t.realizedPnL ?? 0), 0), [closed]);
  // Live unrealized across open positions (skip positions with no mark yet).
  const openPnl = useMemo(() => open.reduce((s, t) => s + toUsd(unrealized(t) ?? 0, t.symbol), 0), [open]);
  const wins = closed.filter(t => (t.realizedPnL ?? 0) > 0).length;
  const winRate = closed.length ? Math.round((wins / closed.length) * 100) : 0;
  const enabledCount = rules.filter(r => r.enabled).length;

  // Per-strategy aggregation: realized P&L + trade counts.
  const byRule = useMemo(() => {
    const m = new Map<string, { name: string; enabled: boolean; n: number; openN: number; pnl: number }>();
    for (const r of rules) m.set(r.id, { name: r.name, enabled: r.enabled, n: 0, openN: 0, pnl: 0 });
    for (const t of trades) {
      const row = m.get(t.ruleId) ?? { name: t.ruleId, enabled: false, n: 0, openN: 0, pnl: 0 };
      if (OPEN_STATUSES.has(t.status)) row.openN++;
      else { row.n++; row.pnl += t.realizedPnL ?? 0; }
      m.set(t.ruleId, row);
    }
    return Array.from(m.values()).sort((a, b) => b.pnl - a.pnl);
  }, [rules, trades]);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground p-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading forex paper activity…</div>;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div className="text-xs text-muted-foreground">
            Forex paper fills are <span className="font-medium text-foreground">simulated in-process</span> (no FXCM account) —
            P&amp;L is shown in USD (open-position hover shows the pair's quote-currency amount). Live arming is on the Live tab.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={refreshing}>
          {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          <span className="ml-1.5">Refresh</span>
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-500">{error}</div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="Open positions" value={String(open.length)} />
        <Stat label="Open P&L (USD)" value={openPnl.toLocaleString('en-US', { maximumFractionDigits: 0 })} cls={pnlClass(openPnl)} sub="live · unrealized" />
        <Stat label="Closed trades" value={String(closed.length)} />
        <Stat label="Realized P&L (USD)" value={realized.toLocaleString('en-US', { maximumFractionDigits: 0 })} cls={pnlClass(realized)} />
        <Stat label="Win rate" value={`${winRate}%`} sub={`${enabledCount} strategies enabled`} />
      </div>

      {/* Per-strategy */}
      <Card>
        <CardHeader className="py-2"><CardTitle className="text-sm">Forex strategies</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table head={['Strategy', 'State', 'Open', 'Closed', 'P&L (USD)']}>
            {byRule.map((r, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="px-3 py-1.5">{r.name}</td>
                <td className="px-3 py-1.5">
                  <span className={r.enabled ? 'text-green-500' : 'text-muted-foreground'}>{r.enabled ? 'Enabled' : 'Disabled'}</span>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.openN || '-'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.n || '-'}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${pnlClass(r.pnl)}`}>{r.n ? r.pnl.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '-'}</td>
              </tr>
            ))}
            {byRule.length === 0 && <EmptyRow cols={5} text="No forex strategies." />}
          </Table>
        </CardContent>
      </Card>

      {/* Open positions */}
      <Card>
        <CardHeader className="py-2"><CardTitle className="text-sm">Open positions ({open.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table head={['Symbol', 'Dir', 'Entry', 'Mark', 'Stop', 'Target', 'Lots', 'P&L (USD)', 'Opened', 'Signal', 'Action']}>
            {open.map(t => {
              const u = unrealized(t);
              const closing = closingIds.has(t.id);
              const tp = t.currentTakeProfitPrice ?? t.initialTakeProfitPrice;
              return (
              <tr key={t.id} className="border-t border-border/40 cursor-pointer hover:bg-muted/40" onClick={() => setChartTrade(t)} onKeyDown={e => rowActivate(e, t)} role="button" tabIndex={0} title="Show chart">
                <td className="px-3 py-1.5 font-medium">{dispSymbol(t.symbol)}</td>
                <td className="px-3 py-1.5"><Dir d={t.direction} /></td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtRate(t.entryPrice, t.symbol)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{t.lastPrice && t.lastPrice > 0 ? fmtRate(t.lastPrice, t.symbol) : '-'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtRate(t.currentStopPrice, t.symbol)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{tp != null ? fmtRate(tp, t.symbol) : <span className="text-muted-foreground" title="No fixed target — exit is the trailing stop">trail</span>}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtLots(t.quantity)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${pnlClass(usdPnl(u, t.symbol))}`}>
                  {u != null
                    ? <Tooltip content={`${fmtPnl(u, t.symbol)} (quote ccy)`}><span>{fmtUsd(u, t.symbol)}</span></Tooltip>
                    : '-'}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtAge(t.entryTime)}</td>
                <td className="px-3 py-1.5 max-w-[260px] whitespace-normal break-words text-muted-foreground">{t.entryReason ?? '-'}</td>
                <td className="px-3 py-1.5 text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    disabled={closing}
                    title="Close this position at market"
                    onClick={e => { e.stopPropagation(); void closeTrade(t); }}
                    onKeyDown={e => e.stopPropagation()}
                  >
                    {closing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Close'}
                  </Button>
                </td>
              </tr>
              );
            })}
            {open.length === 0 && <EmptyRow cols={11} text="No open forex positions." />}
          </Table>
        </CardContent>
      </Card>

      {/* Recent closed trades */}
      <Card>
        <CardHeader className="py-2"><CardTitle className="text-sm">Recent trades</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table head={['Exit', 'Symbol', 'Dir', 'Entry', 'Exit px', 'Lots', 'P&L (USD)', 'R', 'Reason']}>
            {closed.slice(0, 100).map(t => (
              <tr key={t.id} className="border-t border-border/40 cursor-pointer hover:bg-muted/40" onClick={() => setChartTrade(t)} onKeyDown={e => rowActivate(e, t)} role="button" tabIndex={0} title="Show chart">
                <td className="px-3 py-1.5 text-muted-foreground">{fmtNY(t.exitTime)}</td>
                <td className="px-3 py-1.5 font-medium">{dispSymbol(t.symbol)}</td>
                <td className="px-3 py-1.5"><Dir d={t.direction} /></td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtRate(t.entryPrice, t.symbol)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtRate(t.exitPrice, t.symbol)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtLots(t.quantity)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${pnlClass(usdDirect(t.realizedPnL))}`}>
                  {fmtUsdDirect(t.realizedPnL)}
                </td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${pnlClass(t.realizedRMultiple)}`}>
                  {t.realizedRMultiple != null ? `${t.realizedRMultiple.toFixed(2)}R` : '-'}
                </td>
                <td className="px-3 py-1.5 text-muted-foreground">{t.exitReason ?? '-'}</td>
              </tr>
            ))}
            {closed.length === 0 && <EmptyRow cols={9} text="No closed forex trades yet." />}
          </Table>
        </CardContent>
      </Card>

      <TradeChartDialog trade={chartTrade} onClose={() => setChartTrade(null)} />
    </div>
  );
};

// ── Small presentational helpers ─────────────────────────────────
const Stat: React.FC<{ label: string; value: string; sub?: string; cls?: string }> = ({ label, value, sub, cls }) => (
  <div className="rounded-md border border-border/60 bg-card/50 px-3 py-2">
    <div className="text-[11px] text-muted-foreground">{label}</div>
    <div className={`text-lg font-semibold tabular-nums ${cls ?? ''}`}>{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
  </div>
);

const Dir: React.FC<{ d?: string }> = ({ d }) => (
  d === 'Short'
    ? <span className="inline-flex items-center text-red-500"><ArrowDown className="h-3 w-3" />S</span>
    : <span className="inline-flex items-center text-green-500"><ArrowUp className="h-3 w-3" />L</span>
);

const Table: React.FC<{ head: string[]; children: React.ReactNode }> = ({ head, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs">
      <thead>
        <tr className="text-muted-foreground">
          {head.map((h, i) => (
            <th key={i} className={`px-3 py-1.5 font-medium ${i === 0 || h === 'Symbol' || h === 'Strategy' || h === 'Dir' || h === 'Signal' || h === 'Reason' ? 'text-left' : 'text-right'}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const EmptyRow: React.FC<{ cols: number; text: string }> = ({ cols, text }) => (
  <tr><td colSpan={cols} className="px-3 py-6 text-center text-muted-foreground">{text}</td></tr>
);

export default ForexAutomationPanel;
